import type { SelectChangeEvent, SxProps, Theme } from "@mui/material";
import { ListSubheader, MenuItem, Select } from "@mui/material";
import type { Field } from "../../api/types/objectTypes";

const FIELD_VALUE_SEPARATOR = "\x1F"; // ASCII Unit Separator — cannot appear in user input

export interface ObjectFilterFieldGroup<TGroupKey extends string = string> {
  key: TGroupKey;
  label: string;
  fields: Field[];
}

interface ObjectFilterFieldSelectValue<TGroupKey extends string = string> {
  groupKey?: TGroupKey;
  fieldId: string;
}

interface ObjectFilterFieldSelection<TGroupKey extends string = string> {
  groupKey: TGroupKey;
  field: Field;
}

interface Props<TGroupKey extends string = string> {
  value?: ObjectFilterFieldSelectValue<TGroupKey>;
  fieldGroups: ObjectFilterFieldGroup<TGroupKey>[];
  onChange: (selection: ObjectFilterFieldSelection<TGroupKey>) => void;
  sx?: SxProps<Theme>;
}

const getOptionValue = (groupKey: string, fieldId: string): string => `${groupKey}${FIELD_VALUE_SEPARATOR}${fieldId}`;

const getOptionLabel = <TGroupKey extends string>(group: ObjectFilterFieldGroup<TGroupKey>, field: Field): string =>
  group.label ? `${group.label}.${field.name}` : field.name;

const ObjectFilterFieldSelect = <TGroupKey extends string = string>({
  value,
  fieldGroups,
  onChange,
  sx,
}: Props<TGroupKey>) => {
  const groupsWithFields = fieldGroups.filter((group) => group.fields.length > 0);

  const selectedGroup =
    value?.groupKey !== undefined
      ? groupsWithFields.find((group) => group.key === value.groupKey)
      : groupsWithFields.find((group) => group.fields.some((field) => field.id === value?.fieldId));

  const selectedField = selectedGroup?.fields.find((field) => field.id === value?.fieldId);
  const selectedValue = selectedGroup && selectedField ? getOptionValue(selectedGroup.key, selectedField.id) : "";

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    if (!newValue) {
      return;
    }

    const separatorIndex = newValue.indexOf(FIELD_VALUE_SEPARATOR);
    if (separatorIndex < 0) {
      return;
    }

    const groupKey = newValue.slice(0, separatorIndex);
    const fieldId = newValue.slice(separatorIndex + 1);
    const group = groupsWithFields.find((g) => g.key === groupKey);
    const field = group?.fields.find((f) => f.id === fieldId);
    if (group && field) {
      onChange({ groupKey: group.key, field });
    }
  };

  return (
    <Select
      value={selectedValue}
      onChange={handleChange}
      sx={sx}
      renderValue={() => (selectedGroup && selectedField ? getOptionLabel(selectedGroup, selectedField) : "")}
    >
      {groupsWithFields.flatMap((group) => [
        ...(group.label
          ? [
              <ListSubheader key={`${group.key}_header`} sx={{ lineHeight: 2, pb: 0.5, pl: 2, pt: 1.5 }}>
                {group.label}
              </ListSubheader>,
            ]
          : []),
        ...group.fields.map((field) => (
          <MenuItem
            key={`${group.key}_${field.id}`}
            value={getOptionValue(group.key, field.id)}
            sx={group.label ? { pl: 3.5 } : undefined}
          >
            {field.name}
          </MenuItem>
        )),
      ])}
    </Select>
  );
};

export default ObjectFilterFieldSelect;
